/*
=============================================
   SCORES  (professor portal view)
=============================================
The grid is the base view - students down, assignments across - and the same
filters the export uses (class, assignments, students) narrow it. Clicking any
assignment column, or any cell, opens that deployment's full roster.

WHY THIS IS NOT THE EXPORT VIEW. Export reads the live score row, which is
overwritten by practice forever after and carries no class. This reads the grade
RECORD table: one immutable row per attempt, from a Submit press or a finalized
timed run, with the late penalty recomputed on read against the current due date.
The two answer different questions and both are worth having.

⚠️ FOUR THINGS THAT ARE NOT SCORES, shown as states rather than as blanks,
because "no score" has four different fixes:
   running    a clock is open right now
   no case    a run started and the case never reached them (a platform fault)
   opened     they opened the workbook and never pressed Start
   (blank)    they never appeared
*/

import { api } from "../scripts/api.js";
import { Picker } from "./export.js";

const esc = (t) => String(t ?? "").replace(/[<>&]/g, "");
const PCT = (v) => (v == null ? "" : `${Math.round(100 * v)}%`);
const MIN = (s) => (s == null ? "" : `${(s / 60).toFixed(0)}m`);
const AZ = (ts) => (ts ? new Date(ts).toLocaleString(undefined,
   { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// The cell's short label and the class that colours it. Kept in one place so the
// grid, the legend and the roster cannot describe the same state differently.
const STATE = {
   scored:      { cls: "sc-ok" },
   in_progress: { label: "running", cls: "sc-live", tip: "a clock is open right now" },
   failed_start:{ label: "no case", cls: "sc-bad", tip: "started, but the case was never delivered" },
   run_no_score:{ label: "no score", cls: "sc-warn" },
   opened_only: { label: "opened",  cls: "sc-warn", tip: "opened the workbook, never pressed Start" },
   not_started: { label: "",        cls: "sc-none" },
   none:        { label: "",        cls: "sc-none" },
   submitted:   { cls: "sc-ok" },
   expired:     { cls: "sc-ok" },
   finished_no_score: { label: "no score", cls: "sc-warn" },
};

/*
⚠️ THE ELEMENT IS `app-class-scores`, NOT `app-scores`. components/score.js (the
STUDENT "My scores" view) already registers `app-scores`, and every component
ships in ONE bundle, so a second registration of that name throws
NotSupportedError at import time and takes the whole bundle down with it -
including the login page's click handlers, which is how this was found on
2026-09-12: the sign-in buttons rendered and did nothing.

The lesson, and it is general: a custom element name is a GLOBAL name across the
whole app, not a per-page one. Grep `customElements.define` before adding one.
*/
class AppClassScores extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <h1 class="page-title">Scores</h1>
         <p class="page-sub">Every enrolled student and every assignment attached to the
            class. A cell is the score of record, with late penalties applied as your due
            dates stand right now. Click an assignment name to see who did what.</p>

         <div class="sc-bar">
            <label>Class <select id="sc-class" class="sc-select"></select></label>
            <label>Assignments <span id="sc-assign"></span></label>
            <label>Students <span id="sc-students"></span></label>
            <button id="sc-csv" class="sc-btn" disabled>Download CSV</button>
         </div>

         <div id="sc-status" class="sc-status"></div>

         <div id="sc-grid-wrap" hidden>
            <div class="sc-sheet">
               <table class="sc-table"><thead id="sc-head"></thead><tbody id="sc-body"></tbody></table>
            </div>
            <p class="sc-legend">
               <span class="sc-swatch sc-live"></span>running now
               <span class="sc-swatch sc-bad"></span>started, case never delivered
               <span class="sc-swatch sc-warn"></span>opened, never started
               <span class="sc-swatch sc-none"></span>nothing recorded
               <span class="sc-legend-note">a red corner marks a late attempt; a small number is the attempt count</span>
            </p>
         </div>

         <div id="sc-detail" hidden></div>`;

      this.classSel = this.querySelector("#sc-class");
      this.classSel.addEventListener("change", () => this.load({ resetPickers: true }));
      this.assignPick = new Picker(this.querySelector("#sc-assign"), "assignments", () => this.load());
      this.studentPick = new Picker(this.querySelector("#sc-students"), "students", () => this.load());
      this.querySelector("#sc-csv").addEventListener("click", () => this.downloadCsv());
      this.loadClasses();
   }

   // Called by the router when the view is shown.
   show() {
      if (this.classSel && this.classSel.value) this.load();
   }

   status(text, kind = "") {
      const el = this.querySelector("#sc-status");
      el.textContent = text || "";
      el.className = "sc-status " + kind;
   }

   async loadClasses() {
      try {
         const r = await api("/list-classes");
         if (!r.ok) throw new Error(`list-classes ${r.status}`);
         const { classes = [] } = await r.json();
         if (!classes.length) { this.status("No classes yet."); return; }
         this.classSel.innerHTML = classes.map(c =>
            `<option value="${c.class_id}">${esc(c.course_code)} · ${esc(c.section)} — ${esc(c.title || "")}</option>`
         ).join("");
         this.load({ resetPickers: true });
      } catch {
         this.status("Could not load your classes.", "err");
      }
   }

   query(extra = {}) {
      const p = new URLSearchParams({ class_id: this.classSel.value, ...extra });
      const a = this.assignPick && this.assignPick.param();
      const s = this.studentPick && this.studentPick.param();
      if (a !== undefined) p.set("assignments", a);
      if (s !== undefined) p.set("students", s);
      return p.toString();
   }

   async load({ resetPickers = false } = {}) {
      const classId = this.classSel.value;
      if (!classId) return;
      this.closeDetail();
      this.status("Loading…");
      this.querySelector("#sc-csv").disabled = true;
      try {
         const qs = resetPickers
            ? new URLSearchParams({ class_id: classId }).toString()
            : this.query();
         const r = await api(`/scores/grid?${qs}`);
         if (r.status === 403) { this.status("You do not teach that class.", "err"); return; }
         if (!r.ok) throw new Error(`scores ${r.status}`);
         const data = await r.json();

         // Options come back unfiltered, so repopulating cannot trap the picker
         // in its own selection.
         if (resetPickers || !this.assignPick.items.length) {
            this.assignPick.setItems((data.assignments ?? []).map(a => ({
               id: a.assignment_id, label: a.title,
               note: a.timing_mode === "async" ? "timed" : "",
            })));
            this.studentPick.setItems((data.students ?? []).map(s => ({
               id: s.user_id, label: s.student, note: "",
            })));
         }
         this.data = data;
         this.render();
      } catch (err) {
         this.status("Could not load scores.", "err");
         console.error("scores load failed", err);
      }
   }

   render() {
      const d = this.data;
      const wrap = this.querySelector("#sc-grid-wrap");
      const assignments = (d.assignments || []).filter(a => d.shown.assignments.includes(a.assignment_id));
      const students = (d.students || []).filter(s => d.shown.students.includes(s.user_id));

      if (!assignments.length || !students.length) {
         wrap.hidden = true;
         const filtered = this.assignPick.param() !== undefined || this.studentPick.param() !== undefined;
         this.status(filtered
            ? "No rows match the current filters."
            : "Nothing to show: this class has no assignments attached, or no students enrolled.");
         return;
      }
      this.status("");
      wrap.hidden = false;
      this.querySelector("#sc-csv").disabled = false;

      const cellBy = new Map((d.cells || []).map(c => [`${c.user_id}:${c.assignment_id}`, c]));

      /*
      DIAGONAL COLUMN HEADERS. Assignment titles run 30-60 characters ("INDEX and
      MATCH: The Classic Two-Way Lookup"); horizontally they force either a wide
      column or a tall wrapped header, and both wreck a grid of 13 assignments.
      Rotated 45 degrees the label length costs nothing but header height, which
      is paid once for the whole sheet - the same trick a spreadsheet uses.

      The rotation lives on an inner span, never on the <th> itself: rotating a
      table cell breaks its participation in the table's layout, and with
      position:sticky it stops sticking.
      */
      this.querySelector("#sc-head").innerHTML =
         `<tr><th class="sc-corner">Student</th>${assignments.map((a, i) => `
            <th class="sc-diag" style="z-index:${Math.max(10, 90 - i)}" title="${esc(a.title)}">
               <div class="sc-diag-inner"><button class="sc-col" data-assignment="${a.assignment_id}"
                  >${esc(a.title)}${a.timing_mode === "async"
                     ? ` <span class="sc-timed">${Math.round((a.duration_secs || 0) / 60)}m</span>` : ""}</button></div>
            </th>`).join("")}</tr>`;

      this.querySelector("#sc-body").innerHTML = students.map(s =>
         `<tr><th class="sc-rowhead" title="${esc(s.student)}">${esc(s.student)}</th>${assignments.map(a => {
            const c = cellBy.get(`${s.user_id}:${a.assignment_id}`) || { status: "none" };
            const st = STATE[c.status] || STATE.none;
            const label = c.status === "scored" ? PCT(c.pct) : (st.label || "");
            const tip = c.status === "scored"
               ? `${PCT(c.pct)}${c.attempts > 1 ? `, best of ${c.attempts} attempts` : ""}${c.any_late ? ", late" : ""}`
               : c.status === "in_progress" && c.remaining_secs != null
                  ? `running, ${MIN(c.remaining_secs)} left`
                  : (st.tip || "");
            const late = c.status === "scored" && c.any_late ? `<i class="sc-late" title="late attempt"></i>` : "";
            const many = c.attempts > 1 ? `<sup class="sc-att" title="${c.attempts} attempts">${c.attempts}</sup>` : "";
            const live = "";   // the clock reads in the tooltip and the roster
            return `<td class="sc-c ${st.cls}"><button class="sc-cell" data-assignment="${a.assignment_id}"
                        data-student="${s.user_id}" title="${esc(s.student)} — ${esc(a.title)}${tip ? ": " + esc(tip) : ""}"
                        >${label}${many}${live}${late}</button></td>`;
         }).join("")}</tr>`
      ).join("");

      this.querySelectorAll("[data-assignment]").forEach(btn =>
         btn.addEventListener("click", () => this.openDetail(
            Number(btn.dataset.assignment), btn.dataset.student ? Number(btn.dataset.student) : null)));
   }

   closeDetail() {
      const el = this.querySelector("#sc-detail");
      el.hidden = true;
      el.innerHTML = "";
   }

   async openDetail(assignmentId, highlightUserId) {
      const el = this.querySelector("#sc-detail");
      el.hidden = false;
      el.innerHTML = `<p class="sc-status">Loading…</p>`;
      try {
         const r = await api(`/scores/assignment?class_id=${this.classSel.value}&assignment_id=${assignmentId}`);
         if (!r.ok) throw new Error(`detail ${r.status}`);
         const d = await r.json();
         const dep = d.deployment, sum = d.summary;

         const bar = [
            `${sum.scored} of ${sum.enrolled} scored`,
            sum.in_progress ? `${sum.in_progress} running now` : "",
            sum.failed_start ? `${sum.failed_start} never received the case` : "",
            sum.opened_only ? `${sum.opened_only} opened but never started` : "",
            sum.not_started ? `${sum.not_started} never started` : "",
         ].filter(Boolean).join(" · ");

         const stats = sum.scored
            ? `mean ${PCT(sum.mean)} · median ${PCT(sum.median)} · high ${PCT(sum.high)} · low ${PCT(sum.low)}`
            : "";

         el.innerHTML = `
            <div class="sc-detail-head">
               <button class="sc-back" id="sc-close">← Back to the grid</button>
               <h2>${esc(dep.title)}</h2>
               <p class="sc-sub">${dep.timing_mode === "async"
                  ? `timed, ${Math.round((dep.duration_secs || 0) / 60)} minutes, `
                    + `${dep.max_attempts ? `${dep.max_attempts} attempt${dep.max_attempts > 1 ? "s" : ""}` : "unlimited attempts"} · ` : ""}
                  ${dep.due_at ? `due ${AZ(dep.due_at)}` : "no due date"} ·
                  grade of record: ${d.of_record}</p>
               <p class="sc-summary">${bar}${stats ? ` — ${stats}` : ""}</p>
            </div>
            <div class="sc-table-wrap">
               <table class="sc-table sc-roster">
                  <thead><tr><th>Student</th><th>Status</th><th>Started</th><th>Time</th>
                     <th>Correct</th><th>Points</th><th>Score</th><th>Attempts</th><th></th></tr></thead>
                  <tbody>${d.roster.map(r => {
                     const st = STATE[r.status] || STATE.none;
                     const note = r.status === "failed_start"
                        ? "case never delivered"
                        : r.status === "opened_only" ? `opened ${r.opens}× ${AZ(r.first_open)}`
                        : r.score && r.score.days_late ? `${r.score.days_late}d late, −${Math.round(100 * r.score.penalty)}%`
                        : r.status === "in_progress" ? `${MIN(r.remaining_secs)} left` : "";
                     return `<tr class="${highlightUserId === r.user_id ? "sc-hi" : ""}">
                        <td class="sc-name">${esc(r.student)}</td>
                        <td class="${st.cls}">${esc(r.status.replace(/_/g, " "))}</td>
                        <td>${AZ(r.started_at)}</td>
                        <td>${MIN(r.elapsed_secs)}</td>
                        <td>${r.correct_count != null ? `${r.correct_count}/${r.total_count ?? "?"}` : ""}</td>
                        <td>${r.points_earned != null ? `${r.points_earned}/${r.points_total ?? "?"}` : ""}</td>
                        <td>${r.score ? PCT(r.score.pct) : ""}</td>
                        <td>${r.attempts_started || ""}</td>
                        <td class="sc-muted">${esc(note)}</td>
                     </tr>`;
                  }).join("")}</tbody>
               </table>
            </div>`;
         this.querySelector("#sc-close").addEventListener("click", () => this.closeDetail());
         el.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (err) {
         el.innerHTML = `<p class="sc-status err">Could not load that assignment.</p>`;
         console.error("scores detail failed", err);
      }
   }

   /*
   Download through a normal navigation, like the export view: the route sets
   Content-Disposition, the session cookie rides along, and nothing has to be
   re-implemented client side.
   */
   downloadCsv() {
      if (!this.classSel.value) return;
      const base = location.hostname === "localhost" ? "https://localhost:3001" : "https://api.gridprof.com";
      window.location.href = `${base}/scores/grid?${this.query({ format: "csv" })}`;
   }
}

customElements.define("app-class-scores", AppClassScores);
